import React, { useEffect, useState } from 'react';
import { getUsers, createUser, updateUser, deleteUser } from './api';

function AdminPage() {
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ username: '', email: '', full_name: '', is_admin: false, deactivate_at: '', disabled: false });
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null); // Username of user being edited

  const fetchUsers = async () => {
    try {
      const res = await getUsers();
      setUsers(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const userToSubmit = { ...newUser };
      if (!userToSubmit.deactivate_at) delete userToSubmit.deactivate_at;
      else userToSubmit.deactivate_at = new Date(userToSubmit.deactivate_at).toISOString();
      
      if (editingUser) {
        await updateUser(editingUser, userToSubmit, password);
        setEditingUser(null);
      } else {
        await createUser(userToSubmit, password);
      }
      
      setNewUser({ username: '', email: '', full_name: '', is_admin: false, deactivate_at: '', disabled: false });
      setPassword('');
      fetchUsers();
    } catch (err) {
      alert(editingUser ? 'Failed to update user' : 'Failed to create user');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user.username);
    setNewUser({
      username: user.username,
      email: user.email || '',
      full_name: user.full_name || '',
      is_admin: user.is_admin,
      deactivate_at: user.deactivate_at ? user.deactivate_at.slice(0, 16) : '',
      disabled: user.disabled || false
    });
    setPassword(''); // Password blank means unchanged
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setNewUser({ username: '', email: '', full_name: '', is_admin: false, deactivate_at: '', disabled: false });
    setPassword('');
  };

  const handleDelete = async (username) => {
    if (!window.confirm(`Delete user ${username}?`)) return;
    const deleteData = window.confirm('Delete user data as well?');
    try {
      await deleteUser(username, deleteData);
      fetchUsers();
    } catch (err) {
      alert('Failed to delete user');
    }
  };

  return (
    <div className="container">
      <h2>User Management</h2>
      
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3>{editingUser ? 'Edit User' : 'Create User'}</h3>
        <form onSubmit={handleCreate} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Username</label>
            <input className="input" style={{ width: '100%' }} value={newUser.username} onChange={e => setNewUser({...newUser, username: e.target.value})} required disabled={!!editingUser} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Email</label>
            <input className="input" style={{ width: '100%' }} type="email" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Full Name</label>
            <input className="input" style={{ width: '100%' }} value={newUser.full_name} onChange={e => setNewUser({...newUser, full_name: e.target.value})} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Password {editingUser && '(Leave blank to keep)'}</label>
            <input className="input" style={{ width: '100%' }} type="password" value={password} onChange={e => setPassword(e.target.value)} required={!editingUser} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Deactivate At</label>
            <input className="input" style={{ width: '100%' }} type="datetime-local" value={newUser.deactivate_at} onChange={e => setNewUser({...newUser, deactivate_at: e.target.value})} />
          </div>
          <div style={{ marginBottom: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" checked={newUser.is_admin} onChange={e => setNewUser({...newUser, is_admin: e.target.checked})} />
              Is Admin
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" checked={newUser.disabled} onChange={e => setNewUser({...newUser, disabled: e.target.checked})} />
              Disabled
            </label>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="submit" className="btn btn-primary">{editingUser ? 'Update' : 'Create'}</button>
            {editingUser && <button type="button" onClick={handleCancelEdit} className="btn btn-secondary">Cancel</button>}
          </div>
        </form>
      </div>

      <div className="card">
        <h3>Users</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Username</th>
                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Email</th>
                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Role</th>
                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Status</th>
                <th style={{ textAlign: 'right', padding: '0.5rem' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.username} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '0.5rem' }}>{user.username}</td>
                  <td style={{ padding: '0.5rem' }}>{user.email}</td>
                  <td style={{ padding: '0.5rem' }}>{user.is_admin ? 'Admin' : 'User'}</td>
                  <td style={{ padding: '0.5rem' }}>{user.disabled ? 'Disabled' : 'Active'}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                    <button onClick={() => handleEdit(user)} className="btn btn-secondary btn-sm" style={{ marginRight: '0.5rem' }}>Edit</button>
                    {!user.is_admin && (
                      <button onClick={() => handleDelete(user.username)} className="btn btn-danger btn-sm">Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AdminPage;
